import { format, parseISO, startOfToday } from 'date-fns';
import { enUS, es, fr, it, pl } from 'date-fns/locale';
import { CalendarIcon, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdatePromiseDate } from '@/api/domains/markets';
import type { DeliveryTimeOption, Market, PromiseDateSettings } from '@/api/types/services';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { resolveLocale } from '@/i18n';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { defaultPromiseDate, DELIVERY_TIME_OPTIONS } from '@/lib/promise-date';
import { TIMEZONE_OPTIONS } from '@/lib/timezones';
import { cn } from '@/lib/utils';

import { CountryFlags } from '../markets/components/CountryFlags';
import { PromiseDatePreview } from './PromiseDatePreview';

const localeMap = { en: enUS, pl, fr, es, it } as const;
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * One market section (Figma 712-15983): collapsible header (name + flags + chevron)
 * over a grey panel holding the cut-off config and the promise-date preview. Each
 * section owns its own draft state and Save (per-market persistence).
 */
export function PromiseDateMarketSection({ market }: { market: Market }) {
  const { t, i18n } = useTranslation('widgets');
  const locale = resolveLocale(i18n.language);
  // Tolerate a stale cached market shape that predates the promise_date field.
  const pd = market.promise_date ?? defaultPromiseDate(market.countries);

  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(pd.active);
  const [cutoffActive, setCutoffActive] = useState(pd.cutoff_active);
  const [shipmentDays, setShipmentDays] = useState<number[]>([...pd.shipment_days]);
  const [deadlineHour, setDeadlineHour] = useState(pd.deadline_hour);
  const [deadlineMinute, setDeadlineMinute] = useState(pd.deadline_minute);
  const [timezone, setTimezone] = useState(pd.timezone);
  const [deliveryTime, setDeliveryTime] = useState<DeliveryTimeOption>(pd.delivery_time);
  const [excludedDates, setExcludedDates] = useState<string[]>([...pd.excluded_dates]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const update = useUpdatePromiseDate(market.id);

  const minuteValues = Array.from(
    new Set([...Array.from({ length: 12 }, (_, i) => i * 5), deadlineMinute]),
  ).sort((a, b) => a - b);

  const draft: PromiseDateSettings = {
    active,
    cutoff_active: cutoffActive,
    shipment_days: shipmentDays,
    deadline_hour: deadlineHour,
    deadline_minute: deadlineMinute,
    timezone,
    delivery_time: deliveryTime,
    excluded_dates: excludedDates,
  };

  const toggleDay = (day: number) => {
    setShipmentDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b),
    );
  };

  const addExcludedDate = (date: Date | undefined) => {
    setDatePickerOpen(false);
    if (!date) return;
    const key = format(date, 'yyyy-MM-dd');
    setExcludedDates((prev) => (prev.includes(key) ? prev : [...prev, key].sort()));
  };

  const removeExcludedDate = (key: string) => {
    setExcludedDates((prev) => prev.filter((d) => d !== key));
  };

  // "1st May" within the current year, "1st May 2026" when it falls in another year.
  const formatExcludedDate = (key: string) => {
    const date = parseISO(key);
    const pattern = date.getFullYear() === new Date().getFullYear() ? 'do MMMM' : 'do MMMM yyyy';
    return format(date, pattern, { locale: localeMap[locale] });
  };

  const handleSave = () => {
    update.mutate(
      {
        active,
        config: {
          cutoff_active: cutoffActive,
          shipment_days: shipmentDays,
          deadline_hour: deadlineHour,
          deadline_minute: deadlineMinute,
          timezone,
          delivery_time: deliveryTime,
          excluded_dates: excludedDates,
        },
      },
      {
        onSuccess: () => toast.success(t('promiseDate.saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <section>
      <div className="border-t border-[#ededed]" />
      <div className="flex items-center justify-between py-4">
        <div className="flex min-w-0 flex-col gap-[3px]">
          <h3 className="truncate text-xl font-medium tracking-[-0.6px] text-black">
            {market.name}
          </h3>
          <CountryFlags codes={market.countries} />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? t('promiseDate.collapse') : t('promiseDate.expand')}
          className="flex h-9 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent"
        >
          <ChevronUp className={cn('size-6 transition-transform', !open && 'rotate-180')} />
        </button>
      </div>
      <div className="border-t border-[#ededed]" />

      {open ? (
        <div className="bg-[#f7f7f7] px-6 py-8">
          <div className="mx-auto flex w-full max-w-[608px] flex-col gap-3.5">
            <h4 className="text-xl font-medium tracking-[-0.6px] text-black">
              {t('promiseDate.cutoff.title')}
            </h4>

            <SwitchRow
              checked={cutoffActive}
              onCheckedChange={setCutoffActive}
              label={t('promiseDate.active')}
              ariaLabel={t('promiseDate.cutoff.toggleLabel')}
            />

            <p className="text-sm font-medium text-[#0a0a0a]">
              {t('promiseDate.cutoff.shipmentDays')}
            </p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => {
                const selected = shipmentDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleDay(day)}
                    className={cn(
                      'flex size-[50px] items-center justify-center rounded-[4px] text-xs font-semibold transition-colors',
                      selected
                        ? 'border border-black bg-white text-[#0a0a0a]'
                        : 'bg-[#f2f2f2] text-muted-foreground hover:bg-[#ebebeb]',
                    )}
                  >
                    {t(`promiseDate.days.${day}`)}
                  </button>
                );
              })}
            </div>

            <p className="text-sm font-medium text-[#0a0a0a]">
              {t('promiseDate.cutoff.orderDeadline')}
            </p>

            <div className="flex flex-wrap items-center gap-[22px]">
              <div className="flex items-center gap-1.5">
                <Select
                  value={String(deadlineHour)}
                  onValueChange={(v) => setDeadlineHour(Number(v))}
                >
                  <SelectTrigger className="w-[72px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {pad2(h)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm font-medium text-black">:</span>
                <Select
                  value={String(deadlineMinute)}
                  onValueChange={(v) => setDeadlineMinute(Number(v))}
                >
                  <SelectTrigger className="w-[72px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {minuteValues.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {pad2(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="w-[273px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-sm font-medium text-[#0a0a0a]">
              {t('promiseDate.cutoff.deliveryTime')}
            </p>
            <Select
              value={deliveryTime}
              onValueChange={(v) => {
                if ((DELIVERY_TIME_OPTIONS as string[]).includes(v)) {
                  setDeliveryTime(v as DeliveryTimeOption);
                }
              }}
            >
              <SelectTrigger className="w-[273px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_TIME_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`promiseDate.deliveryTimes.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-[#0a0a0a]">
                {t('promiseDate.cutoff.excludeDates')}
              </p>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full max-w-[398px] justify-start font-normal text-muted-foreground"
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    {t('promiseDate.cutoff.addDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    onSelect={addExcludedDate}
                    disabled={{ before: startOfToday() }}
                    locale={localeMap[locale]}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-sm text-muted-foreground">
                {t('promiseDate.cutoff.excludeDatesHint')}
              </p>
              {excludedDates.length > 0 ? (
                <div className="flex flex-wrap gap-2.5">
                  {excludedDates.map((date) => (
                    <span
                      key={date}
                      className="inline-flex h-6 items-center gap-1 rounded-[10px] bg-[#ebebeb] px-2 text-sm font-medium text-[#171717]"
                    >
                      {formatExcludedDate(date)}
                      <button
                        type="button"
                        aria-label={t('promiseDate.cutoff.removeDate')}
                        onClick={() => removeExcludedDate(date)}
                        className="text-muted-foreground hover:text-black"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="my-2 h-px bg-[#e0e0e0]" />

            <h4 className="text-xl font-medium tracking-[-0.6px] text-black">
              {t('promiseDate.promise.title')}
            </h4>
            <p className="max-w-[390px] text-base text-muted-foreground">
              {t('promiseDate.promise.description')}
            </p>
            <SwitchRow
              checked={active}
              onCheckedChange={setActive}
              label={t('promiseDate.active')}
              ariaLabel={t('promiseDate.promise.toggleLabel')}
            />

            <p className="text-sm font-medium text-[#0a0a0a]">{t('promiseDate.promise.preview')}</p>
            <PromiseDatePreview settings={draft} />

            <Button
              type="button"
              onClick={handleSave}
              disabled={update.isPending}
              className="mt-2 w-fit bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('promiseDate.save')}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function SwitchRow({
  checked,
  onCheckedChange,
  label,
  ariaLabel,
}: {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  label: string;
  ariaLabel: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={ariaLabel} />
      <span className="text-sm font-medium text-[#171717]">{label}</span>
    </div>
  );
}
